<template>
  <div class="permission-panel">
    <div class="permission-header" v-if="currentMenuPermission">
      <h3>
        {{ $i18n('Current Menu Permission') }}:
        {{ currentMenuPermission }}
      </h3>
    </div>
    <s-tree
      ref="filterTree"
      class="no-menu-tree"
      node-key="id"
      :props="defaultProps"
      :data="noMenuPermissionData"
    />

    <s-table :data="permissionData" processValue="-">
      <s-table-column
        v-for="col in basicColumns"
        :key="col.prop"
        v-bind="col"
        :width="col.width"
      >
      </s-table-column>
      <s-table-column :label="$i18n('Action')" width="120" fixed="right">
        <template slot-scope="scope">
          <s-button
            v-if="shouldHighlightButton(scope.row.value)"
            type="text"
            size="small"
            @click="togglePermissionHighlight(scope.row.value)"
          >
            {{
              isPermissionHighlighted(scope.row.value)
                ? $i18n('UnHighlight')
                : $i18n('Highlight')
            }}
          </s-button>
        </template>
      </s-table-column>
    </s-table>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import router from '@/router';
import {
  convertRouteNameToPermission,
  permissionStorage,
} from '@/utils/permissionStorage';
import { permissionHighlighter } from '@/utils/permissionHighlighter';
import { getMenuTree } from '@/api/basic/displaySetting';
import { reportEvent } from '@/utils/qms';

interface PermissionData {
  value: string;
  index: number;
  hasPermission: string;
}

@Component
export default class PermissionPanel extends Vue {
  permissionData: PermissionData[] = [];

  currentMenuPermission = '';

  highlightedPermissions: Set<string> = new Set();

  canHighlightPermissions: string[] = [];

  basicColumns = [
    {
      label: this.$i18n('Index'),
      prop: 'index',
      width: 20,
    },
    {
      label: this.$i18n('Permission Value'),
      prop: 'value',
      width: 400,
    },
    {
      label: this.$i18n('Has Permission'),
      prop: 'hasPermission',
      width: 80,
    },
  ];

  timer: any = null;

  routeChangeTimer: any = null;

  noMenuPermissionData: any[] = [];
  allMenuPermissionData: any[] = [];

  defaultProps = {
    label: (data: any, node: any) => {
      // return `${data.perm.title} (${data.perm.perm_id})`;
      return data.perm.title;
    },
    children: 'children',
  };

  async getMenuTreeData() {
    const res = await getMenuTree(router.currentRoute.name ?? '');
    this.noMenuPermissionData = [
      {
        perm: {
          id: 1,
          title: this.$i18n('No Menu Permission'),
        },
        children: res.data?.children || [],
      },
    ];

    const isMenuHide = router.currentRoute?.meta?.menuHide;
    if (!isMenuHide) {
      this.currentMenuPermission = res.data?.be_key;
    } else {
      this.currentMenuPermission = '';
    }
  }

  mounted() {
    this.loadData();
    this.getMenuTreeData();
    this.timer = setInterval(() => {
      this.loadData();
    }, 3000);
  }

  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    if (this.routeChangeTimer) {
      clearTimeout(this.routeChangeTimer);
    }
    // 清除所有高亮
    this.clearAllHighlight();
  }

  @Watch('$route', { immediate: true, deep: true })
  onRouteChange() {
    this.loadData();
    this.getMenuTreeData();
    // 路由变化时清除所有高亮
    this.clearAllHighlight();

    // 清理之前的定时器（如果存在）
    if (this.routeChangeTimer) {
      clearTimeout(this.routeChangeTimer);
    }
    this.routeChangeTimer = setTimeout(() => {
      permissionHighlighter.findPermissionElements();
      this.canHighlightPermissions = permissionHighlighter.getPermissionList();
    }, 1000);
  }

  shouldHighlightButton(permissionKey: string) {
    return this.canHighlightPermissions.includes(permissionKey);
  }

  loadData() {
    const routeName = router.currentRoute.name ?? null;
    const permissions = permissionStorage.get(routeName);
    this.permissionData = permissions.map((value, index) => ({
      value,
      index: index + 1,
      hasPermission: this.$store.state.permission.userPermission.includes(value)
        ? this.$i18n('Y')
        : this.$i18n('N'),
    }));
  }

  togglePermissionHighlight(permissionKey: string) {
    if (this.highlightedPermissions.has(permissionKey)) {
      // 如果已经高亮，则清除该权限点的高亮
      this.clearPermissionHighlight(permissionKey);
    } else {
      // 如果未高亮，则高亮该权限点
      this.highlightPermission(permissionKey);
      reportEvent('click_support_panel_permission_highlight');
    }
  }

  highlightPermission(permissionKey: string) {
    // 高亮指定权限点的元素（不清除之前的高亮）
    permissionHighlighter.highlight(permissionKey);
    this.highlightedPermissions.add(permissionKey);
  }

  clearPermissionHighlight(permissionKey: string) {
    // 清除指定权限点的高亮（不影响其他权限点的高亮）
    permissionHighlighter.clearPermissionHighlight(permissionKey);
    this.highlightedPermissions.delete(permissionKey);
  }

  isPermissionHighlighted(permissionKey: string): boolean {
    return this.highlightedPermissions.has(permissionKey);
  }

  clearAllHighlight() {
    permissionHighlighter.clearHighlight();
    this.highlightedPermissions.clear();
  }
}
</script>

<style scoped lang="scss">
.permission-panel {
  width: 100%;
  height: 100%;

  .permission-header {
    margin-bottom: 16px;
    border-radius: 4px;
    padding: 12px;
    background-color: #f5f5f5;

    h3 {
      margin: 0;
      font-size: 14px;
      font-weight: 500;
      color: #333333;
    }
  }

  .no-menu-tree {
    margin-bottom: 16px;
  }
}
</style>
